/**
 * The route table.
 *
 * Only shell-owned pages are declared here. Every micro-app route comes from the runtime
 * remote registry (/remotes.json) and is resolved by the `*` catch-all — adding an app is
 * a registry edit, not a change to this file. See src/federation/.
 *
 * `handle.nextPath` carries a route's pattern in Next's bracket form. Ported components
 * still compare against those strings — EnhancedOverlaySidebar checks
 * `router.pathname === '/view/[id]'` to mark a sidebar entry active, and RouteGuard
 * matches allowed hrefs by pathname — so the pattern has to survive the move. Only
 * parameterised routes need it; for a static route the pattern and the URL are the same
 * string, and useShellRouter falls back to the live pathname.
 */

import type { RouteObject } from 'react-router-dom';
import AppShell from '@/AppShell';
import HomeRedirect from '@/pages/HomeRedirect';
import Csi from '@/pages/Csi';
import ServerError from '@/pages/ServerError';
import AgentHub from '@/pages/AgentHub';
import Settings from '@/pages/Settings';
import {
  FinanceHub,
  QhseHub,
  CrewingHub,
} from '@/pages/DomainPlaceholder';
import { CasefilesRoute, ViewRoute } from '@/pages/EmbeddedPageRoute';
import RegistryRoute from './RegistryRoute';

export const routes: RouteObject[] = [
  {
    path: '/',
    element: <AppShell />,
    // Reached when a loader or a render throws above the in-page boundaries — the
    // equivalent of Next serving pages/500.
    errorElement: <ServerError />,
    children: [
      { index: true, element: <HomeRedirect /> },

      // Agent Hub (shell-owned, not federated).
      { path: 'agent-hub', element: <AgentHub /> },
      { path: 'agent-hub/finance', element: <FinanceHub /> },
      { path: 'agent-hub/qhse', element: <QhseHub /> },
      { path: 'agent-hub/crewing', element: <CrewingHub /> },

      // Opened from the header's gear. Under /agent-hub, so RouteGuard already treats it
      // as shell-owned and it needs no menu record.
      { path: 'agent-hub/settings', element: <Settings /> },

      // Not federated: CSI is a third-party app on another domain that will not accept a
      // SIYA JWT, so the page exchanges one for a short-lived embed URL first.
      { path: 'csi', element: <Csi /> },

      // DB-driven iframe routes: the URL comes from the agenthub_master_menu record.
      { path: 'casefiles', element: <CasefilesRoute /> },
      { path: 'view/:id', element: <ViewRoute />, handle: { nextPath: '/view/[id]' } },

      // Micro-apps from the remote registry, else NotFound. Static paths above always win.
      { path: '*', element: <RegistryRoute /> },
    ],
  },
];
